import {
  ButtonHTMLAttributes,
  forwardRef,
  ForwardRefExoticComponent,
  MouseEvent,
  ReactNode,
  Ref,
  RefAttributes,
} from 'react';
import noop from 'lodash/noop';
import styled, { css } from 'styled-components';

import { Loader } from 'shared/components/Loader';
import { variableFontRegular } from 'styles/theme/text';

interface IButtonStatic {
  Button: any;
  Loader: any;
  LoaderWrapper: any;
  Title: any;
}

export interface IButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  'data-qa': string;
  children?: ReactNode;
  className?: string;
  inverted?: boolean;
  loading?: boolean;
  primary?: boolean;
  secondary?: boolean;
  selected?: boolean;
  title?: string;
}

type ButtonComponent = ForwardRefExoticComponent<IButtonProps & RefAttributes<HTMLButtonElement>> &
  IButtonStatic;

export const Button = forwardRef(
  (
    { children, loading, onClick, title, type, value, ...rest }: IButtonProps,
    ref?: Ref<HTMLButtonElement>,
  ) => {
    const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
      if (onClick) {
        onClick(event);
      }
    };

    return (
      <Button.Button
        onClick={!loading ? handleClick : noop}
        ref={ref}
        type={type || 'button'}
        value={value || title}
        {...rest}
      >
        {loading ? (
          <Button.LoaderWrapper data-qa="button_loader" className="buttonLoader">
            <Button.Loader />
          </Button.LoaderWrapper>
        ) : (
          <span>{title}</span>
        )}
        {!loading && children}
      </Button.Button>
    );
  },
) as ButtonComponent;

Button.Button = styled.button<{
  disabled?: boolean;
  inverted?: boolean;
  primary?: boolean;
  secondary?: boolean;
}>`
  align-items: center;
  cursor: pointer;
  display: flex;
  flex-grow: 1;
  height: 42px;
  justify-content: center;
  outline: none;
  padding: 0;
  text-transform: uppercase;
  user-select: none;
  width: 100%;
  ${variableFontRegular({ size: '11px', height: '12px' })};

  ${({ theme, disabled, inverted }) => {
    if (disabled || inverted) {
      return css`
        background-color: ${theme.navigation.backgroundColor};
        border: ${theme.navigation.border};
        color: ${theme.navigation.textColor};
      `;
    }
    return css`
      background-color: ${theme.navigation.invertedBackgroundColor};
      border: ${theme.navigation.invertedBorder};
      color: ${theme.navigation.invertedTextColor};
    `;
  }};

  & > span {
    padding-top: 2px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.small}px) {
    ${variableFontRegular({ size: '11.5px', height: '12px' })};
  }
`;

Button.LoaderWrapper = styled.div`
  align-items: center;
  display: flex;
  height: 100%;
  justify-content: center;
  width: 100%;
  padding: 16px;
`;

Button.Loader = styled(Loader)`
  width: 30%;
`;
